import { Box, HStack, Skeleton, Table, Text } from '@chakra-ui/react'

import { useRefreshCountdown } from '../../hooks/useRefreshCountdown'
import { useHotelsI18n } from '../../i18n'
import type { CallHistoryEntry } from '../../types/callHistory'
import { valueOrPlaceholder } from '../../utils/display'
import { formatCallTime } from '../../utils/formatting'

interface IncomingCallsTableProps {
  calls: CallHistoryEntry[]
  isLoading?: boolean
}

export const IncomingCallsTable = ({
  calls,
  isLoading = false,
}: IncomingCallsTableProps) => {
  const DISPLAY_LIMIT = 100
  const SKELETON_ROWS = 8
  const { text } = useHotelsI18n()
  const safeCalls = Array.isArray(calls) ? calls : []
  const visibleCalls = safeCalls.slice(0, DISPLAY_LIMIT)
  const secondsLeft = useRefreshCountdown(60)

  return (
    <Box
      borderWidth={1}
      borderColor="border.default"
      borderRadius="lg"
      bg="bg.default"
      overflow="hidden"
    >
      <Box maxH="400px" overflowY="auto" overflowX="auto">
        <Table.Root size="sm" variant="line">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader
                px={3}
                py={1.5}
                position="sticky"
                top={0}
                bg="bg.subtle"
                zIndex={1}
              >
                {text('hotels.dashboard.number', 'Number')}
              </Table.ColumnHeader>
              <Table.ColumnHeader
                px={3}
                py={1.5}
                position="sticky"
                top={0}
                bg="bg.subtle"
                zIndex={1}
              >
                {text('hotels.dashboard.name', 'Name')}
              </Table.ColumnHeader>
              <Table.ColumnHeader
                px={3}
                py={1.5}
                position="sticky"
                top={0}
                bg="bg.subtle"
                zIndex={1}
              >
                {text('hotels.dashboard.time', 'Time')}
              </Table.ColumnHeader>
              <Table.ColumnHeader
                px={3}
                py={1.5}
                position="sticky"
                top={0}
                bg="bg.subtle"
                zIndex={1}
              >
                {text('hotels.dashboard.destination', 'Dest.')}
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <Table.Row key={`incoming-skeleton-${index}`}>
                  <Table.Cell px={3} py={1.5}>
                    <Skeleton h="14px" w="90px" />
                  </Table.Cell>
                  <Table.Cell px={3} py={1.5}>
                    <Skeleton h="14px" w="120px" />
                  </Table.Cell>
                  <Table.Cell px={3} py={1.5}>
                    <Skeleton h="14px" w="140px" />
                  </Table.Cell>
                  <Table.Cell px={3} py={1.5}>
                    <Skeleton h="14px" w="56px" />
                  </Table.Cell>
                </Table.Row>
              ))
            ) : visibleCalls.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={4} textAlign="center" py={8}>
                  <Text color="fg.muted">
                    {text(
                      'hotels.dashboard.noIncomingCalls',
                      'No incoming calls',
                    )}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              visibleCalls.map((call) => (
                <Table.Row key={call.id}>
                  <Table.Cell px={3} py={1.5}>
                    <Text fontSize="xs">
                      {valueOrPlaceholder(call.mango_source_number, text)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell px={3} py={1.5}>
                    <Text fontSize="xs" truncate>
                      {valueOrPlaceholder(call.source_name, text)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell px={3} py={1.5}>
                    <Text fontSize="xs" whiteSpace="nowrap">
                      {formatCallTime(call.call_time)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell px={3} py={1.5}>
                    <Text fontSize="xs">
                      {valueOrPlaceholder(call.dst_ext, text)}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      <HStack
        justify="space-between"
        px={4}
        py={2}
        borderTopWidth={1}
        borderColor="border.default"
        bg="bg.subtle"
      >
        <Text fontSize="xs" color="fg.subtle">
          {isLoading
            ? text('hotels.dashboard.loadingCalls', 'Loading calls...')
            : text(
                'hotels.dashboard.latestCallsCount',
                'Showing latest {{count}} calls',
                {
                  count: visibleCalls.length,
                },
              )}
        </Text>
        <Text fontSize="xs" color="fg.subtle">
          {text('hotels.dashboard.refreshingIn', 'Refreshing in {{seconds}}s', {
            seconds: secondsLeft,
          })}
        </Text>
      </HStack>
    </Box>
  )
}
